import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ROUTE_CONSTANTS } from '../constants/route.constant';
import { UserRoles } from '../enums/user-roles.enum';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';

/** Route guard: allows access when `route.data.roles` matches the current user role. */
@Injectable({ providedIn: 'root' })
export class RolesGuard implements CanActivate {
    constructor(
        private readonly authService: AuthApiService,
        private readonly router: Router
    ) {}

    /** Redirects to login or forbidden when the user lacks `route.data.roles`. */
    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        const requiredRoles = route.data?.['roles'] as UserRoles[];

        // If no roles required, allow
        if (!requiredRoles || requiredRoles.length === 0) {
            return of(true);
        }

        return this.authService.user$.pipe(
            take(1),
            map((user) => {
                // If user not logged in, redirect
                if (!user) {
                    this.router.navigate([ROUTE_CONSTANTS.AUTH.ROOT, ROUTE_CONSTANTS.AUTH.LOGIN]);
                    return false;
                }

                // Check if user has all of the required roles
                const hasAccess = requiredRoles.some((role) => user.role.includes(role));
                if (!hasAccess) {
                    this.router.navigate([ROUTE_CONSTANTS.FORBIDDEN]);
                }

                return hasAccess;
            }),
            catchError(() => {
                this.router.navigate([ROUTE_CONSTANTS.AUTH.ROOT, ROUTE_CONSTANTS.AUTH.LOGIN]);
                return of(false);
            })
        );
    }
}
